import React from "react";
import { MapPin, Sun, Moon, Sunset } from "lucide-react";

// ─── Shared tone system ───────────────────────────────────────────────
// Every pill in every table (Shift / Status / Gender) is built from this
// single palette, so changing a color here changes it everywhere at once.

export type BadgeTone =
  | "indigo"
  | "violet"
  | "teal"
  | "amber"
  | "emerald"
  | "rose"
  | "sky"
  | "slate";

const toneClasses: Record<BadgeTone, string> = {
  indigo: "bg-indigo-50 text-indigo-700",
  violet: "bg-violet-50 text-violet-700",
  teal: "bg-teal-50 text-teal-700",
  amber: "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
  rose: "bg-rose-50 text-rose-700",
  sky: "bg-sky-50 text-sky-700",
  slate: "bg-slate-100 text-slate-600",
};

const dotClasses: Record<BadgeTone, string> = {
  indigo: "bg-indigo-500",
  violet: "bg-violet-500",
  teal: "bg-teal-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  rose: "bg-rose-500",
  sky: "bg-sky-500",
  slate: "bg-slate-400",
};

interface DotBadgeProps {
  label: string;
  tone: BadgeTone;
  icon?: React.ReactNode;
}

/** Base pill: light tint background + colored dot (or icon) + semibold label. */
export const DotBadge: React.FC<DotBadgeProps> = ({ label, tone, icon }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${toneClasses[tone]}`}
  >
    {icon ?? <span className={`w-1.5 h-1.5 rounded-full ${dotClasses[tone]}`} />}
    {label}
  </span>
);

// ─── Hash-based color fallback ─────────────────────────────────────────
// For free-text values that aren't in a fixed lookup (custom shift names
// like "First Half", site ids, etc.) — same key always maps to the same
// tone, so a given shift/site is visually consistent everywhere without
// needing a hardcoded entry. This replaces the hash-color logic that used
// to be copy-pasted per page (Labor.tsx, Shift.tsx).

const HASH_TONES: BadgeTone[] = ["amber", "indigo", "teal", "emerald", "sky", "violet", "rose"];

export const getToneForKey = (key: string): BadgeTone => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return HASH_TONES[Math.abs(hash) % HASH_TONES.length];
};

// ─── Shift badge ───────────────────────────────────────────────────────
// Works with known names ("Day" / "Night" / "Afternoon" / "Day Shift" /
// "Night Shift") using fixed colors + icons, and falls back to a stable
// hashed color for any other custom shift name (e.g. "First Half",
// "Half Day") so this one component covers every page, including the
// Shift Management screen where names are user-defined.

const SHIFT_TONE: Record<string, BadgeTone> = {
  Day: "amber",
  "Day Shift": "amber",
  Night: "indigo",
  "Night Shift": "indigo",
  Afternoon: "teal",
};

const SHIFT_ICON: Record<string, React.ReactNode> = {
  Day: <Sun size={12} />,
  "Day Shift": <Sun size={12} />,
  Night: <Moon size={12} />,
  "Night Shift": <Moon size={12} />,
  Afternoon: <Sunset size={12} />,
};

interface ShiftBadgeProps {
  shift?: string;
  /** Optional stable key (e.g. shift id) to hash for unknown shift names.
   * Falls back to the shift name itself when omitted. */
  hashKey?: string;
}

export const ShiftBadge: React.FC<ShiftBadgeProps> = ({ shift, hashKey }) => {
  if (!shift) return <span className="text-slate-300 text-xs">--</span>;
  const tone = SHIFT_TONE[shift] || getToneForKey(hashKey || shift);
  return <DotBadge label={shift} tone={tone} icon={SHIFT_ICON[shift]} />;
};

// ─── Status badge ──────────────────────────────────────────────────────
// Covers every status word used across Wage / Attendance / Labour /
// Site / Shift tables. Add new keys here rather than inventing new
// inline styles per page.

const STATUS_TONE: Record<string, BadgeTone> = {
  Paid: "emerald",
  Active: "emerald",
  "On-Time": "emerald",
  Completed: "sky",
  Due: "amber",
  "On Hold": "amber",
  "Half Day": "sky",
  Absent: "slate",
  Inactive: "rose",
  "On Leave": "slate",
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <DotBadge label={status.toUpperCase()} tone={STATUS_TONE[status] || "slate"} />
);

// ─── Gender badge ──────────────────────────────────────────────────────

const GENDER_TONE: Record<string, BadgeTone> = {
  Male: "indigo",
  Female: "rose",
  Other: "slate",
};

export const GenderBadge: React.FC<{ gender: string }> = ({ gender }) => (
  <DotBadge label={gender} tone={GENDER_TONE[gender] || "slate"} />
);

// ─── Site label ──────────────────────────────────────────────────────
// Plain text + pin icon (not a pill) — matches the screenshot, where Site
// is the one column that stays as text rather than a colored badge.

export const SiteLabel: React.FC<{ label: string; sub?: string }> = ({ label, sub }) => (
  <div className="flex items-center gap-1.5 text-slate-600">
    <MapPin size={14} className="text-emerald-500 shrink-0" />
    <div>
      <p className="text-sm font-medium leading-tight">{label}</p>
      {sub && <p className="text-xs text-slate-400 leading-tight">{sub}</p>}
    </div>
  </div>
);

// ─── Avatar (initials circle) ───────────────────────────────────────
// Same rotating palette used for every "Name / Employee" column.

export const avatarPalette = [
  "bg-rose-100 text-rose-700",
  "bg-indigo-100 text-indigo-700",
  "bg-amber-100 text-amber-800",
  "bg-emerald-100 text-emerald-700",
  "bg-sky-100 text-sky-700",
  "bg-violet-100 text-violet-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
];

export const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

interface AvatarCellProps {
  name: string;
  subLabel: string; // e.g. employee id, or role
  paletteIndex: number;
}

export const AvatarCell: React.FC<AvatarCellProps> = ({ name, subLabel, paletteIndex }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${avatarPalette[paletteIndex % avatarPalette.length]}`}
    >
      {getInitials(name)}
    </div>
    <div>
      <p className="font-semibold text-slate-800 leading-tight">{name}</p>
      <p className="text-xs text-slate-400">{subLabel}</p>
    </div>
  </div>
);

// ─── Icon cell (square variant) ─────────────────────────────────────
// Same title/subtitle layout as AvatarCell, but for non-person entities
// (a site, a shift definition) — a rounded-lg icon box instead of a
// circle. Use this instead of styling a one-off icon box per page.

interface IconCellProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  tone?: BadgeTone;
}

export const IconCell: React.FC<IconCellProps> = ({ icon, title, subtitle, tone = "indigo" }) => (
  <div className="flex items-center gap-3">
    <div
      className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${toneClasses[tone]}`}
    >
      {icon}
    </div>
    <div className="min-w-0">
      <p className="font-semibold text-slate-800 leading-tight truncate">{title}</p>
      {subtitle && <p className="text-xs text-slate-400 leading-tight">{subtitle}</p>}
    </div>
  </div>
);